import shortcuts from './list';
import SHORTCUT_TYPES from './shortcut-types';
import keycode from 'keycode';
import _ from 'lodash';
import { EventEmitter } from 'events';

const isMac = navigator.userAgent.indexOf('Mac OS X') !== -1;

var REPLACE_REGS = [
  { reg: /^super/i, val: isMac ? '⌘' : 'ctrl' },
  { reg: /alt/i, val: isMac ? '⌥' : 'alt' },
  { reg: /shift/i, val: '⇧' },

  { reg: /up/i, val: '↑' },
  { reg: /down/i, val: '↓' },
  { reg: /left/i, val: '←' },
  { reg: /right/i, val: '→' }
];

const SKIP_TYPES = [SHORTCUT_TYPES.textEditor];

const RESTRICTED_TAGS = {
  INPUT: true,
  TEXTAREA: true
};

const RESTRICTED_ROLES = {
  textbox: true,
  formbutton: true
};

type ShortcutModel = {
  id: String,
  label: string,
  desc: string,
  // List of keys used for shorcut
  keys: string[],
  // Human readable keys string
  formated: string[],
  ignore?: Boolean
};

class Shortcuts extends EventEmitter {
  altDown: boolean;
  ctrlDown: boolean;
  superDown: boolean;
  list: ShortcutModel = [];
  constructor() {
    super();
    this.keymap = {};
    for (let id in shortcuts) {
      const shortcut = shortcuts[id];
      shortcut.id = id;
      this.list.push(shortcut);

      if (SKIP_TYPES.includes(shortcut.type) || shortcut.ignore) continue;
      shortcut.formated = [];
      for (var key of shortcut.keys) {
        var format = key.toUpperCase();

        for (var replace of REPLACE_REGS) {
          format = format.replace(replace.reg, replace.val);
        }

        shortcut.formated.push(format);
        this.keymap[key] = id;
      }
    }
  }

  keysToString(key: string): string {
    for (const replace of REPLACE_REGS) {
      key = key.replace(replace.reg, replace.val).replace(/\+/g, ' + ');
    }
    return key;
  }

  getFormated(key: string): string {
    return shortcuts[key].formated;
  }

  onKey(keys: string[] | string, callback: Function, unrestricted: Boolean): void {
    if (_.isString(keys)) keys = [keys];
    for (var key of keys) {
      this.on((unrestricted ? 'unrestrictedkey:' : 'key:') + key, callback);
    }
  }

  offKey(keys: string[] | string, callback: Function) {
    if (_.isString(keys)) keys = [keys];
    for (var key of keys) {
      this.removeListener('key:' + key, callback);
      this.removeListener('unrestrictedkey:' + key, callback);
    }
  }

  off(event, callback) {
    this.removeListener(event, callback);
  }

  enable() {
    if (!this.isEnabled) {
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('keyup', this.onKeyUp);

      this.isEnabled = true;
    }
  }

  disable() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    this.isEnabled = false;
  }

  getEventId(e) {
    var res = keycode(e.which);
    if (e.shiftKey) res = 'shift+' + res;
    if (e.altKey) res = 'alt+' + res;
    if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
      res = 'super+' + res;
    }
    return res;
  }

  getList(): ShortcutModel[] {
    return this.list;
  }

  getTypes() {
    return SHORTCUT_TYPES;
  }

  onKeyUp = e => {
    this.altDown = e.altKey;
    this.ctrlDown = e.ctrlKey;
    this.shiftDown = e.shiftKey;
    this.metaDown = e.metaKey;
  };

  onKeyDown = (e: KeyboardEvent) => {
    this.altDown = e.altKey;
    this.ctrlDown = e.ctrlKey;
    this.shiftDown = e.shiftKey;
    this.metaDown = e.metaKey;

    const { activeElement } = e.target.ownerDocument || document;
    const keyId = this.getEventId(e);

    this.emit('unrestrictedkey:' + keyId, e, keyId);
    if (e.defaultPrevented) return;

    if (
      !RESTRICTED_TAGS[activeElement.tagName] &&
      !RESTRICTED_ROLES[activeElement.getAttribute('role')] &&
      !isFormElement(activeElement)
    ) {
      this.emit('key:' + keyId, e, keyId);

      if (e.defaultPrevented) return;

      const shortcutId = this.keymap[keyId];

      if (shortcutId) {
        const shortcut: ShortcutModel = shortcuts[shortcutId];
        if (shortcut.default) {
          e.preventDefault();
        }
        this.emit(shortcutId, e, shortcut);
      }
    }
  };
}

function isFormElement(element: Element) {
  if (element.tagName === 'FORM') return true;
  else if (element.parentElement) return isFormElement(element.parentElement);
  else return false;
}

export default new Shortcuts();
